import { createContext, useState, useCallback } from "react";

const ErrorHandlerContext = createContext();

const DEFAULT_NOTIFICATION_DURATION = 5000; // 5 seconds

function ErrorHandlerContextProvider({ children }) {
    const [errorList, setErrorList] = useState([]);

    /**
     * Add a notification with auto-dismiss support
     * @param {string|object} notification - Message string or object with { msg, type, duration }
     */
    const addNotification = useCallback((notification) => {
        const id = Date.now() + Math.random();
        const notificationWithId = typeof notification === "string" 
            ? { id, msg: notification, type: "error" }
            : { id, type: "error", ...notification };
        
        setErrorList(prev => [...prev, notificationWithId]);
        
        // Auto-dismiss after duration (default 5s for success, 8s for error)
        const duration = notificationWithId.duration ?? (
            notificationWithId.type === "success" 
                ? DEFAULT_NOTIFICATION_DURATION 
                : DEFAULT_NOTIFICATION_DURATION + 3000
        );
        
        setTimeout(() => {
            setErrorList(prev => prev.filter(n => n.id !== id));
        }, duration);
        
        return id;
    }, []);

    /**
     * Remove a specific notification by ID
     */
    const removeNotification = useCallback((id) => {
        setErrorList(prev => prev.filter(n => n.id !== id));
    }, []);

    /**
     * Clear all notifications
     */
    const clearNotifications = useCallback(() => {
        setErrorList([]);
    }, []);

    const value = {
        errorList,
        setErrorList,
        addNotification,
        removeNotification,
        clearNotifications
    };

    return (
        <ErrorHandlerContext.Provider value={value}>
            {children}
        </ErrorHandlerContext.Provider>
    );
}

export { ErrorHandlerContext, ErrorHandlerContextProvider };
